"use client";

import type { AlgoliaProductCard } from "@raumagarn/formatters";
import { SearchResponse } from "algoliasearch";
import { useMemo } from "react";

export const useAlgoliaAutoCompleteSuggestions = ({
	query,
	data,
}: {
	query?: string;
	data: SearchResponse<AlgoliaProductCard> | undefined;
}) => {
	const autoCompleteSuggestions = useMemo(() => {
		if (!data?.hits || !query || query.length < 2) return [];

		const suggestions = new Set<string>();
		const queryLower = query.toLowerCase().trim();

		const matchingNames: string[] = [];

		data.hits.forEach((hit) => {
			if (hit.name && hit.name.toLowerCase().includes(queryLower)) {
				matchingNames.push(hit.name);
			}
		});

		// Sort by relevance: exact matches first, then starts with, then contains
		matchingNames.sort((a, b) => {
			const aLower = a.toLowerCase();
			const bLower = b.toLowerCase();

			// Exact match gets highest priority
			if (aLower === queryLower) return -1;
			if (bLower === queryLower) return 1;

			// Starts with gets second priority
			if (aLower.startsWith(queryLower) && !bLower.startsWith(queryLower))
				return -1;
			if (bLower.startsWith(queryLower) && !aLower.startsWith(queryLower))
				return 1;

			// Then by length (shorter = more specific)
			return a.length - b.length;
		});

		matchingNames.forEach((name) => {
			if (suggestions.size < 8) {
				suggestions.add(name);
			}
		});

		return Array.from(suggestions);
	}, [data?.hits, query]);

	return {
		autoCompleteSuggestions: autoCompleteSuggestions.slice(0, 5),
	};
};
